import { useContext, useState } from "react";
import EducationalHubApi from "../../../api/educationalHubApi";
import Input from "../../../components/Input/Input";
import ModalWindow from "../../../components/ModalWindow/ModalWindow";
import StandartButton from "../../../components/StandartButton/StandartButton";
import { ContextProvider } from "../../../contextProvider";
import { EducationalHub } from "../../../types/EducationalHub";
import { TypeUserData } from "../../../types/TypeUsers";
import { MultiLangText } from "../../../utils/profileDiffUtils";
import Notify, { SuccesNotify } from "../../../utils/toaster";
import { normalizeHubSubdomain } from "./hubManagementUtils";
import UserSearchSelect from "./UserSearchSelect";
import s from "./HubManagement.module.css";

type CreateHubModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (hub: EducationalHub) => void;
};

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
  { code: "uk", label: "UK" },
];

const emptyLangMap = () =>
  LANGUAGES.reduce<Record<string, string>>((acc, lang) => {
    acc[lang.code] = "";
    return acc;
  }, {});

const CreateHubModal = ({ isOpen, onClose, onCreated }: CreateHubModalProps) => {
  const token = localStorage.getItem("token");
  const { userData } = useContext(ContextProvider);
  const [owner, setOwner] = useState<TypeUserData | null>(null);
  const [activeLang, setActiveLang] = useState("en");
  const [titles, setTitles] = useState(emptyLangMap);
  const [briefDescriptions, setBriefDescriptions] = useState(emptyLangMap);
  const [descriptions, setDescriptions] = useState(emptyLangMap);
  const [subdomain, setSubdomain] = useState("");
  const [subscriptionCost, setSubscriptionCost] = useState("");
  const [billingInterval, setBillingInterval] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setOwner(null);
    setActiveLang("en");
    setTitles(emptyLangMap());
    setBriefDescriptions(emptyLangMap());
    setDescriptions(emptyLangMap());
    setSubdomain("");
    setSubscriptionCost("");
    setBillingInterval("");
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const mapToMultiLang = (values: Record<string, string>): MultiLangText[] =>
    LANGUAGES.map((lang) => ({
      language: lang.code,
      text: values[lang.code].trim(),
    })).filter((item) => item.text.length > 0);

  const hasTitle = Object.values(titles).some(
    (value) => value.trim().length > 0,
  );

  const handleSubmit = async () => {
    if (!token || !userData?._id) {
      Notify("Authorization required");
      return;
    }
    if (!owner?._id) {
      Notify("Select an Educational Hub owner");
      return;
    }
    if (!hasTitle) {
      Notify("Enter an Educational Hub title");
      return;
    }

    setIsSubmitting(true);
    try {
      const title = mapToMultiLang(titles);
      const briefDescription = mapToMultiLang(briefDescriptions);
      const description = mapToMultiLang(descriptions);
      const cost =
        subscriptionCost.trim() !== "" ? Number(subscriptionCost) : undefined;
      const interval =
        billingInterval.trim() !== "" ? Number(billingInterval) : undefined;

      const response = await EducationalHubApi.createHubForUserByAdmin(token, {
        userId: userData._id,
        targetHubOwnerId: owner._id,
        newHub: {
          title,
          ...(briefDescription.length ? { briefDescription } : {}),
          ...(description.length ? { description } : {}),
          ...(subdomain.trim() ? { subdomain: subdomain.trim() } : {}),
        },
        ...(cost !== undefined || interval !== undefined
          ? {
              subscription: {
                ...(cost !== undefined ? { cost } : {}),
                ...(interval !== undefined ? { billingInterval: interval } : {}),
              },
            }
          : {}),
      });

      if (!response.status || !response.hub) {
        Notify(response.message || "Failed to create Educational Hub");
        return;
      }

      SuccesNotify("Educational Hub created successfully");
      resetForm();
      onCreated(response.hub);
    } catch (error) {
      console.error("Error creating Educational Hub:", error);
      Notify("Failed to create Educational Hub");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalWindow
      isOpen={isOpen}
      alternativeCloseFunction={handleClose}
      title="Create Educational Hub"
      width="640px"
      maxWidth="calc(100svw - 32px)"
      bgColor="#121318"
    >
      <div className={s.createModal}>
        <UserSearchSelect
          label="Owner"
          selectedUser={owner}
          onSelect={setOwner}
          onClear={() => setOwner(null)}
          required
          disabled={isSubmitting}
        />

        <div className={s.langTabs}>
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              className={`${s.langTab} ${activeLang === lang.code ? s.langTabActive : ""}`}
              onClick={() => setActiveLang(lang.code)}
              disabled={isSubmitting}
            >
              {lang.label}
            </button>
          ))}
        </div>

        <div className={s.formGrid}>
          <Input
            inputValue={titles[activeLang]}
            onChangeInput={(value) =>
              setTitles((prev) => ({ ...prev, [activeLang]: value }))
            }
            lable="Title *"
            placeholder="Educational Hub title..."
            maxLength={80}
            required
            disabled={isSubmitting}
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
          />
          <Input
            inputValue={briefDescriptions[activeLang]}
            onChangeInput={(value) =>
              setBriefDescriptions((prev) => ({
                ...prev,
                [activeLang]: value,
              }))
            }
            lable="Brief description"
            placeholder="Short list description..."
            maxLength={160}
            required={false}
            disabled={isSubmitting}
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
          />
          <label className={s.textareaField}>
            <span className={s.fieldLabel}>Description</span>
            <textarea
              value={descriptions[activeLang]}
              onChange={(event) =>
                setDescriptions((prev) => ({
                  ...prev,
                  [activeLang]: event.target.value,
                }))
              }
              placeholder="Educational Hub description..."
              maxLength={500}
              rows={5}
              disabled={isSubmitting}
            />
          </label>
          <Input
            inputValue={subdomain}
            onChangeInput={(value) => setSubdomain(normalizeHubSubdomain(value))}
            lable="Subdomain"
            placeholder="hub-subdomain"
            maxLength={20}
            required={false}
            disabled={isSubmitting}
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
          />
          <div className={s.inlineGrid}>
            <Input
              inputValue={subscriptionCost}
              onChangeInput={setSubscriptionCost}
              lable="Subscription cost"
              placeholder="0"
              type="number"
              minNumber={0}
              required={false}
              disabled={isSubmitting}
              bgColor="rgba(255, 255, 255, 0.06)"
              border="1px solid rgba(255, 255, 255, 0.12)"
            />
            <Input
              inputValue={billingInterval}
              onChangeInput={setBillingInterval}
              lable="Billing interval"
              placeholder="30"
              type="number"
              minNumber={0}
              required={false}
              disabled={isSubmitting}
              bgColor="rgba(255, 255, 255, 0.06)"
              border="1px solid rgba(255, 255, 255, 0.12)"
            />
          </div>
        </div>

        <div className={s.modalActions}>
          <button
            type="button"
            className={s.resetBtn}
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <StandartButton
            buttonTitle={isSubmitting ? "Creating..." : "Create Hub"}
            action={handleSubmit}
            disabled={isSubmitting || !owner?._id || !hasTitle}
            width="140px"
          />
        </div>
      </div>
    </ModalWindow>
  );
};

export default CreateHubModal;
